import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TruncatedList } from 'react-truncate-list';

import IconMoreFilled from '~icons/ep/more-filled';

import {
	HubTooltip,
	Menu,
	MenuButton,
	MenuLink,
	MenuList,
	MenuPopover,
	SectionDivider,
} from '@adflow/frontend/ui-common';
import { useAbility } from '@adflow/frontend/util-auth';
import { isNotNullish } from '@adflow/shared/util-js';

import type { NavItemProps } from './NavItem';
import { NavItem, navItemStyles } from './NavItem';

type NavItemInfo = Pick<
	NavItemProps,
	'to' | 'end' | 'disabled' | 'disabledCalloutText' | 'children' | 'onClick'
> & {
	children: ReactNode;
	className?: string;
	alternativeAction?: () => void;
};

interface HeaderItemsProps {
	iconButtonStyle: CSSInterpolation;
}

const styles = {
	truncatedList: css({
		display: 'flex',
		alignItems: 'stretch',
		flexGrow: 1,

		'& > li:not([hidden])': {
			display: 'flex',
		},
	}),
};

export function HeaderItems({ iconButtonStyle }: HeaderItemsProps) {
	const { t } = useTranslation(['common', 'permissions']);

	const { ability, missingPermissionText } = useAbility();

	const navItems: NavItemInfo[] = useMemo(
		() => [
			{
				to: '/pacing',
				children: t('sections.pacing'),
				disabled: ability.cannot('list', 'Pacing'),
				disabledCalloutText: missingPermissionText('list', 'Pacing'),
			},
		],
		[ability, missingPermissionText, t]
	);

	const facebookNavItems: NavItemInfo[] = useMemo(
		() =>
			[
				{
					to: '/facebook/templates',
					children: t('sections.facebook-templates'),
					disabled: ability.cannot('list', 'Facebook/Templates'),
					disabledCalloutText: missingPermissionText(
						'list',
						'Facebook/Templates'
					),
				},
				{
					to: '/facebook/media',
					children: t('sections.facebook-media'),
					disabled: ability.cannot('list', 'Facebook/Media'),
					disabledCalloutText: missingPermissionText('list', 'Facebook/Media'),
				},
				{
					to: '/facebook/pages',
					children: t('sections.facebook-pages'),
					disabled: ability.cannot('list', 'Facebook/Pages'),
					disabledCalloutText: missingPermissionText('list', 'Facebook/Pages'),
				},
			].filter(isNotNullish),
		[ability, missingPermissionText, t]
	);

	const googleNavItems: NavItemInfo[] = useMemo(
		() =>
			[
				{
					to: '/google-ads/templates',
					children: t('sections.google-ads-templates'),
					disabled: ability.cannot('list', 'GoogleAds/Templates'),
					disabledCalloutText: missingPermissionText(
						'list',
						'GoogleAds/Templates'
					),
				},
				{
					to: '/google-ads/media',
					children: t('sections.google-ads-media'),
					disabled: ability.cannot('list', 'GoogleAds/Templates'),
					disabledCalloutText: missingPermissionText(
						'list',
						'GoogleAds/Templates'
					),
				},
				{
					to: '/google-ads/keywords',
					children: t('sections.keywords'),
					disabled: ability.cannot('list', 'GoogleAds/Templates'),
					disabledCalloutText: missingPermissionText(
						'list',
						'GoogleAds/Templates'
					),
				},
				{
					to: '/google-ads/promotion-assets',
					children: t('sections.promotion-assets'),
					disabled: ability.cannot('list', 'GoogleAds/Templates'),
					disabledCalloutText: missingPermissionText(
						'list',
						'GoogleAds/Templates'
					),
				},
			].filter(isNotNullish),
		[ability, missingPermissionText, t]
	);

	const gmbNavItems: NavItemInfo[] = useMemo(
		() =>
			[
				{
					to: '/google-my-business/templates',
					children: t('sections.google-my-business-templates'),
					disabled: ability.cannot('list', 'GMB/Templates'),
					disabledCalloutText: missingPermissionText('list', 'GMB/Templates'),
				},
				{
					to: '/google-my-business/media',
					children: t('sections.google-my-business-media'),
					disabled: ability.cannot('list', 'GMB/Media'),
					disabledCalloutText: missingPermissionText('list', 'GMB/Media'),
				},
			].filter(isNotNullish),
		[ability, missingPermissionText, t]
	);

	const renderMenuLink = (item: NavItemInfo) => (
		<MenuLink
			className={item.className}
			to={item.to}
			disabled={item.disabled}
			disabledCalloutText={item.disabledCalloutText}
			key={typeof item.to === 'string' ? item.to : JSON.stringify(item.to)}
		>
			{item.children}
		</MenuLink>
	);

	return (
		<TruncatedList
			css={styles.truncatedList}
			renderTruncator={(props: { hiddenItemsCount: number }) => {
				const hiddenItemsCount = props.hiddenItemsCount;
				if (hiddenItemsCount === 0) return null;
				return (
					<Menu
						trigger={
							<MenuButton css={iconButtonStyle}>
								<IconMoreFilled css={{ width: '1em', height: '1em' }} />
							</MenuButton>
						}
						id="overflow-menu"
					>
						<MenuList>
							{[
								...(hiddenItemsCount === 1
									? []
									: navItems.slice(-(hiddenItemsCount - 1))),
							].map(renderMenuLink)}
							{facebookNavItems.length ? (
								<>
									<SectionDivider text={t('sections.facebook')} />
									{facebookNavItems.map(renderMenuLink)}
								</>
							) : null}
						</MenuList>
					</Menu>
				);
			}}
		>
			{navItems.map((item: NavItemInfo) => (
				<NavItem
					{...item}
					key={typeof item.to === 'string' ? item.to : JSON.stringify(item.to)}
				/>
			))}
			<Menu
				trigger={
					<HubTooltip title={null}>
						<MenuButton
							css={navItemStyles.navLink}
							data-testid="header-Facebok"
						>
							{t('sections.facebook')}
						</MenuButton>
					</HubTooltip>
				}
			>
				<MenuPopover>
					{facebookNavItems.length ? (
						<>{facebookNavItems.map(renderMenuLink)}</>
					) : null}
				</MenuPopover>
			</Menu>
			<Menu
				trigger={
					<HubTooltip title={null}>
						<MenuButton css={navItemStyles.navLink}>
							{t('sections.google-ads')}
						</MenuButton>
					</HubTooltip>
				}
			>
				<MenuPopover>{googleNavItems.map(renderMenuLink)}</MenuPopover>
			</Menu>
			<Menu
				trigger={
					<HubTooltip title={null}>
						<MenuButton css={navItemStyles.navLink}>
							{t('sections.google-my-business')}
						</MenuButton>
					</HubTooltip>
				}
			>
				<MenuPopover>{gmbNavItems.map(renderMenuLink)}</MenuPopover>
			</Menu>
		</TruncatedList>
	);
}
